/**
 * Eligibility badge plus the reasons a row is blocked.
 *
 * The first reason is always visible, because "why" is the whole point of the
 * column and hiding it behind a click would make a 70 row list unreadable. Any
 * further reasons collapse behind a count, since a full bullet list per row is
 * what makes row heights ragged at batch size.
 */

import React, { useState } from 'react';
import { Label } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { eligibilityReason, eligibilityVerdict, i18nKeys } from '../constants';
import { eligibilityReasonText } from '../helpers';
import { RecentChargeEntry, RowEligibility } from '../types';
import styles from '../BulkChargeGuarantee.module.css';

interface EligibilityBadgeProps {
  ambassadorId: number;
  eligibility: RowEligibility;
  recentEntries: RecentChargeEntry[];
  now: number;
}

const EligibilityBadge: React.FC<EligibilityBadgeProps> = ({
  ambassadorId,
  eligibility,
  recentEntries,
  now
}) => {
  const [expanded, setExpanded] = useState(false);

  if (eligibility.verdict === eligibilityVerdict.eligible) {
    return (
      <Label color="green" size="tiny">
        {I18n.t(i18nKeys.ELIGIBILITY.ELIGIBLE)}
      </Label>
    );
  }

  // The double charge guards get a louder colour than an ordinary "not ready".
  const isMoneyRisk =
    eligibility.reasons.includes(eligibilityReason.alreadyCharged) ||
    eligibility.reasons.includes(eligibilityReason.chargedRecently);

  const [firstReason, ...restReasons] = eligibility.reasons;
  const visibleReasons = expanded ? eligibility.reasons : [firstReason];
  const reasonListId = `bulk-charge-reasons-${ambassadorId}`;

  return (
    <div>
      <Label color={isMoneyRisk ? 'red' : 'grey'} size="tiny">
        {I18n.t(i18nKeys.ELIGIBILITY.BLOCKED)}
      </Label>
      <ul className={styles.reasonList} id={reasonListId}>
        {visibleReasons.map((reason) => (
          <li key={reason}>{eligibilityReasonText(reason, ambassadorId, recentEntries, now)}</li>
        ))}
      </ul>
      {restReasons.length > 0 && (
        <button
          type="button"
          className={styles.reasonToggle}
          aria-expanded={expanded}
          aria-controls={reasonListId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? I18n.t(i18nKeys.ELIGIBILITY.SHOW_LESS)
            : I18n.t(i18nKeys.ELIGIBILITY.MORE_REASONS, { count: restReasons.length })}
        </button>
      )}
    </div>
  );
};

export default EligibilityBadge;
